import type {StackSectionEntryType} from './merge-box-status-calculator/stack-section-status'

export const EffectiveEntryStatus = {
  After: 'AFTER',
  Closed: 'CLOSED',
  Draft: 'DRAFT',
  Loading: 'LOADING',
  Merged: 'MERGED',
  Merging: 'MERGING',
  NotReady: 'NOT_READY',
  Queued: 'QUEUED',
  Ready: 'READY',
} as const

export type EffectiveEntryStatus = (typeof EffectiveEntryStatus)[keyof typeof EffectiveEntryStatus]

const UPSTACK_STATES = ['IRRELEVANT_AFTER']

const LOADING_STATES = ['LOADING_MERGE_STATE', 'UNKNOWN_MERGE_STATE']

const MERGING_AND_NON_DYNAMIC_STATES = [
  'CLOSED',
  'IRRELEVANT_DRAFT',
  'IRRELEVANT_MERGED',
  'IRRELEVANT_QUEUED',
  'MERGING',
] as const

const NOT_READY_STATES = [
  'AWAITING_APPROVAL',
  'BLOCKED_MERGE_STATE',
  'BLOCKED_STACK',
  'CHECKS_PENDING',
  'CHECKS_FAILING',
  'ERROR_MERGE_STATE',
  'FAILING_RULES',
  'MERGE_CONFLICTS',
  'WAITING_ON_PREVIOUS',
]

const READY_STATES = ['OK']

const READY_AND_NOT_READY_STATES = [...READY_STATES, ...NOT_READY_STATES]

type MergingAndNonDynamicState = (typeof MERGING_AND_NON_DYNAMIC_STATES)[number]

const MergingAndNonDynamicEffectiveStatus: Record<MergingAndNonDynamicState, EffectiveEntryStatus> = {
  CLOSED: EffectiveEntryStatus.Closed,
  IRRELEVANT_DRAFT: EffectiveEntryStatus.Draft,
  IRRELEVANT_MERGED: EffectiveEntryStatus.Merged,
  IRRELEVANT_QUEUED: EffectiveEntryStatus.Queued,
  MERGING: EffectiveEntryStatus.Merging,
}

function isMergingAndNonDynamicState(status: string): status is MergingAndNonDynamicState {
  return (MERGING_AND_NON_DYNAMIC_STATES as readonly string[]).includes(status)
}

export function determineEffectiveStatus(
  {status, pull}: StackSectionEntryType,
  isRebasing: boolean,
): EffectiveEntryStatus {
  // Temporary EffectiveEntryStatus code until rebuilt in StackSectionStatus in following PR.
  switch (true) {
    case pull.state === 'CLOSED':
      return EffectiveEntryStatus.Closed
    case isMergingAndNonDynamicState(status):
      return MergingAndNonDynamicEffectiveStatus[status]
    case READY_AND_NOT_READY_STATES.includes(status) && isRebasing:
      return EffectiveEntryStatus.NotReady
    case NOT_READY_STATES.includes(status):
      return EffectiveEntryStatus.NotReady
    case READY_STATES.includes(status):
      return EffectiveEntryStatus.Ready
    case UPSTACK_STATES.includes(status):
      return EffectiveEntryStatus.After
    case LOADING_STATES.includes(status):
      return EffectiveEntryStatus.Loading
    default:
      return EffectiveEntryStatus.NotReady
  }
}
